import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, EASE } from "@/components/Reveal";

const FAQS = [
  {
    q: "What is P.E.E.?",
    a: "P.E.E. stands for Position • Event • Entry. It is the foundation of the P.E.E. Game Plan—a structured trading methodology designed to define where a trader should be looking, what market event they are waiting for, and when execution conditions have been satisfied.",
  },
  {
    q: "What is the P.E.E. Game Plan?",
    a: "The P.E.E. Game Plan is the trading methodology taught inside Execution Traders Community. Members learn qualifying positions, market events, entries, risk, targets, validation and invalidation concepts, session rules, trade management, and the execution process built around Position • Event • Entry.",
  },
  {
    q: "What is P.E.E. OS?",
    a: "P.E.E. OS is the technology layer built specifically around the Position • Event • Entry methodology. It helps organize market context, qualifying events, entries, risk levels, trade status, alerts, and analytics into a structured execution process.",
  },
  {
    q: "Do I need to learn the Game Plan to use P.E.E. OS?",
    a: "Yes. P.E.E. OS is designed specifically around the P.E.E. methodology. Members are taught the Game Plan so they understand the logic and execution process behind the information displayed by the OS.",
  },
  {
    q: "Can I use P.E.E. OS with another trading strategy?",
    a: "P.E.E. OS is purpose-built around the P.E.E. Game Plan. It is not intended to function as a universal execution system for unrelated trading strategies.",
  },
  {
    q: "Is P.E.E. OS just another trading indicator?",
    a: "No. P.E.E. OS is designed as an execution operating system rather than a standalone buy/sell indicator. It combines multiple parts of the P.E.E. process into a structured trade lifecycle.",
  },
  {
    q: "Does P.E.E. OS automatically execute trades?",
    a: "No. P.E.E. OS does not automatically place trades on your behalf. The trader remains responsible for execution, risk management, and trading decisions.",
  },
  {
    q: "Does P.E.E. OS guarantee profitable trades?",
    a: "No. Trading involves substantial risk, and no trading methodology or software can guarantee profitable outcomes. P.E.E. OS is designed to support structured execution—not eliminate market risk.",
  },
  {
    q: "What does “Validated” mean?",
    a: "A validated trade has satisfied the defined P.E.E. validation criteria after execution. Validation does not mean the trade is guaranteed to reach TP1 or TP2. A validated trade can still result in a stop loss.",
  },
  {
    q: "What is Execution Traders Community?",
    a: "Execution Traders Community is the training, coaching, accountability, and community environment surrounding P.E.E. Members can learn the Game Plan, access P.E.E. OS, develop execution skills, review trades, study analytics, and train alongside other execution-focused traders.",
  },
  {
    q: "Can beginners join?",
    a: "Yes. The training is designed to teach the P.E.E. methodology from its foundations. Beginners should expect to learn and practice the Game Plan before attempting to use it with real financial risk.",
  },
  {
    q: "What happens after I join?",
    a: "You'll receive instructions for accessing the community and available member resources. From there, you'll begin learning the P.E.E. Game Plan, become familiar with the execution process, and receive instructions for accessing available P.E.E. OS tools and training.",
  },
  {
    q: "What is included with membership?",
    a: "Membership includes access to the P.E.E. ecosystem according to your active plan, including P.E.E. Game Plan Training, P.E.E. OS, Execution Traders Community, Executor Training, analytics, alerts, trade review, coaching, accountability, and member resources as available under the membership.",
  },
  {
    q: "Do I need TradingView?",
    a: "P.E.E. OS currently operates through TradingView. Members are responsible for obtaining any TradingView account or subscription required for their own use unless explicitly stated otherwise.",
  },
  {
    q: "Can I pay the annual membership over time?",
    a: "Eligible customers may be offered flexible payment options at checkout. Availability, approval, payment schedule, interest, and other terms are determined by the applicable payment provider.",
  },
  {
    q: "How does cancellation work?",
    a: "Monthly and annual memberships are governed by the membership terms presented at checkout. Members should review the cancellation, renewal, access, and refund terms before purchasing.",
  },
];

export default function FaqV2() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="border-t border-line bg-[#070809] py-24 lg:py-32">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <SectionHeading
          chapter="12 — FAQ"
          align="center"
          testid="faq-heading"
          title={
            <>
              Questions. <span className="text-neon">Answered.</span>
            </>
          }
        />

        <div className="mt-14">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={0.02 * i}>
              <div data-testid={`faq-item-${i}`} className="border-b border-line">
                <button
                  data-testid={`faq-trigger-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-6 py-5 text-left"
                >
                  <span
                    className={`font-display text-base font-bold tracking-tight transition-colors duration-300 sm:text-lg ${
                      open === i ? "text-neon" : "text-white"
                    }`}
                  >
                    {f.q}
                  </span>
                  <ChevronDown
                    size={17}
                    className={`shrink-0 transition-transform duration-500 ${
                      open === i ? "rotate-180 text-neon" : "text-dim"
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="pb-6 pr-10 text-sm leading-relaxed text-mist"
                      >
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
